import React, { useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { closeDismissOnboardingConfirm, selectDismissOnboardingConfirmOpen } from '../../../../../store/onboarding/onboardingUiSlice'
import { Button } from '../../../../../components/Button/Button'
import { OnboardingStepHeader } from '../../OnboardingStepHeader/OnboardingStepHeader'
import { useTracking } from '../../../../../apps/professional-app/fa/common/tracking/hooks'
import { GrowthTrackEvent } from '../../../../../apps/professional-app/fa/common/tracking'

export interface OnboardingDismissConfirmModalProps {
  onConfirmDismiss: () => Promise<void>
  isDismissing: boolean
  isBusy: boolean
}

export const OnboardingDismissConfirmModal: React.FC<OnboardingDismissConfirmModalProps> = ({ onConfirmDismiss, isDismissing, isBusy }) => {
  const dispatch = useAppDispatch()
  const show = useAppSelector(selectDismissOnboardingConfirmOpen)
  const { trackingClient } = useTracking()

  const handleBack = useCallback(() => {
    trackingClient.track({ eventName: GrowthTrackEvent.ONBOARDING_DISMISS_CONFIRM_BACK_TO_SETUP_CLICKED })
    dispatch(closeDismissOnboardingConfirm())
  }, [dispatch, trackingClient])

  const handleLeaveSetup = useCallback(async () => {
    trackingClient.track({ eventName: GrowthTrackEvent.ONBOARDING_DISMISS_CONFIRM_LEAVE_SETUP_CLICKED })
    await onConfirmDismiss()
  }, [onConfirmDismiss, trackingClient])

  return (
    <Modal show={show} onHide={handleBack} centered contentClassName='rounded-4 overflow-hidden'>
      <Modal.Body className={'p-5'}>
        <OnboardingStepHeader
          icon={<i className='bi bi-rocket-takeoff' />}
          title='Ready to leave setup?'
          subtitle={
            <>
              You can set up any skipped connections later in{' '}
              <NavLink to='/account/integration-settings' className='fw-bold text-dark' onClick={handleBack}>
                Integrations
              </NavLink>
              .
            </>
          }
        />
        <div className='text-center px-5 pt-4 pb-4'>
          <div className='d-flex gap-3 justify-content-center mb-3 text-nowrap'>
            <Button variant='primaryGhost' onClick={handleLeaveSetup} disabled={isBusy} loading={isDismissing}>
              Leave setup
            </Button>
            <Button variant='primary' onClick={handleBack} disabled={isBusy}>
              Back to setup
            </Button>
          </div>
          <div className='d-flex align-items-center justify-content-center gap-1 text-muted'>
            <i className='bi bi-info-circle' />
            <small>
              Questions? Visit the{' '}
              <a href='https://help.zocks.io' target='_blank' rel='noopener noreferrer' className='fw-bold text-muted'>
                Help Center
              </a>
              .
            </small>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
